import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Chip, Avatar } from "@heroui/react";
import { Typography, UniversalTable, IdView } from "../../../../../components";
import { useTranslations } from "../../../../../contexts/translation.context";
import { EmptyContentIc, DoubleArrowRightIc, CalendarIcon } from "../../../../../components/icons";
import { TableColumn } from "../../../../../types/account";
import { staffApi, BalanceHistoryTransaction } from "../../../../../api/staff";
import { formatTimeAgo } from "../../../../../utils/formatTimeAgo";

type WalletActionType =
  | 'reserve'
  | 'reserve_revert'
  | 'campaign_complete'
  | 'invoice_payment'
  | 'balance_top_up'
  | 'guarantee'
  | 'balance_change';

interface BalanceHistoryTabProps {
  creatorId: string;
}

// Вспомогательные функции
const extractHandleFromLink = (link?: string): string | undefined => {
  if (!link) return undefined;

  try {
    const url = new URL(link);
    const [firstSegment] = url.pathname.split('/').filter(Boolean);
    if (firstSegment) {
      return firstSegment.startsWith('@') ? firstSegment : `@${firstSegment}`;
    }
  } catch {
    const match = link.match(/onlyfans\.com\/([^/?#]+)/i);
    if (match?.[1]) {
      const username = match[1];
      return username.startsWith('@') ? username : `@${username}`;
    }
  }

  return undefined;
};

const pickFirstString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

/**
 * BalanceHistoryTab Component
 *
 * Таб с историей баланса криейтора.
 * Показывает таблицу транзакций с изменениями баланса.
 */
const BalanceHistoryTab: React.FC<BalanceHistoryTabProps> = ({ creatorId }) => {
  const { t } = useTranslations();
  const [transactions, setTransactions] = useState<BalanceHistoryTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await staffApi.getCreatorBalanceHistory(creatorId, {
        page: currentPage,
        page_size: pageSize,
      });
      setTransactions(response.objects);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [creatorId, currentPage, pageSize]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const columns: TableColumn[] = [
    { key: "id", name: "ID" },
    { key: "amount", name: t('admin.creators.details.balanceHistory.amount') },
    { key: "action", name: t('admin.creators.details.balanceHistory.action') },
    { key: "date", name: t('admin.creators.details.balanceHistory.date') }
  ];

  const formatShortId = (value?: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.length <= 4) return trimmed;
    return trimmed.slice(-4);
  };

  const formatDate = (dateString: string) => formatTimeAgo(dateString, t);

  const getAmountColor = (amount: number) => {
    return amount >= 0 ? 'text-[#19BE60]' : 'text-[#F31260]';
  };

  const getActionType = (transaction: BalanceHistoryTransaction): WalletActionType => {
    if (transaction.transaction) {
      return transaction.amount >= 0 ? 'balance_top_up' : 'invoice_payment';
    }

    const status = transaction.invoice?.status?.toLowerCase();
    switch (status) {
      case 'hold':
        // Резерв может быть только при отрицательном amount (блокировка средств)
        return transaction.amount < 0 ? 'reserve' : 'balance_top_up';
      case 'refunded':
        // Возврат резерва может быть только при положительном amount
        return transaction.amount >= 0 ? 'reserve_revert' : 'invoice_payment';
      case 'paid':
        return 'campaign_complete';
      case 'guarantee':
        return 'guarantee';
      default:
        // Если положительная сумма - пополнение баланса, если отрицательная - оплата инвойса
        return transaction.amount >= 0 ? 'balance_top_up' : 'invoice_payment';
    }
  };

  const getStatusChip = (actionType: WalletActionType) => {
    const config: Record<WalletActionType, { text: string; color: string }> = {
      reserve: { text: t('wallet.reserve') || 'Reserve', color: '#3B82F6' },
      reserve_revert: { text: t('wallet.reserveRevert') || 'Reserve Revert', color: '#3B82F6' },
      campaign_complete: { text: t('wallet.campaignComplete') || 'Campaign Complete', color: '#17C964' },
      invoice_payment: { text: t('wallet.invoicePayment') || 'Invoice Payment', color: '#7828C8' },
      balance_top_up: { text: t('wallet.balanceTopUpAction') || 'Balance Top Up', color: '#F5A524' },
      guarantee: { text: t('wallet.guarantee') || 'Guarantee', color: '#17C964' },
      balance_change: { text: t('wallet.balanceChange') || 'Balance Change', color: '#71717A' }
    };

    return config[actionType] ?? config.balance_change;
  };

  const renderCell = useCallback((transaction: BalanceHistoryTransaction, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return (
          <IdView textToCopy={transaction.id}>
            {formatShortId(transaction.id) ?? transaction.id}
          </IdView>
        );
      case "amount": {
        const isPositive = transaction.amount >= 0;
        const sign = isPositive ? '+' : '';
        const formattedAmount = Number.isFinite(transaction.amount) ? Math.abs(transaction.amount).toFixed(2) : '0.00';
        const formattedBefore = Number.isFinite(transaction.before) ? transaction.before.toFixed(2) : '0.00';
        const formattedAfter = Number.isFinite(transaction.after) ? transaction.after.toFixed(2) : '0.00';

        return (
          <div className="flex flex-col gap-[4px]">
            <span className={`${getAmountColor(transaction.amount)} text-[14px] font-semibold`}>
              {sign}${formattedAmount}
            </span>
            <div className="flex items-center gap-[4px]">
              <span className="text-[#71717A] text-[14px] font-normal">${formattedBefore}</span>
              <DoubleArrowRightIc />
              <span className="text-[#11181C] text-[14px] font-semibold">${formattedAfter}</span>
            </div>
          </div>
        );
      }
      case "action": {
        const actionType = getActionType(transaction);
        const statusChip = getStatusChip(actionType);
        const actionsWithDetails: WalletActionType[] = ['reserve', 'reserve_revert', 'campaign_complete', 'guarantee'];
        const showOrderSection = actionsWithDetails.includes(actionType);

        // Получаем данные партнера
        const partnerUsername = (transaction.invoice as any)?.partner?.username;
        const partnerHandle = partnerUsername && typeof partnerUsername === 'string' && partnerUsername.trim()
          ? (partnerUsername.trim().startsWith('@') ? partnerUsername.trim() : `@${partnerUsername.trim()}`)
          : extractHandleFromLink(
              transaction.company?.link ||
              (transaction.invoice as any)?.company?.link ||
              (transaction.invoice as any)?.campaign?.link
            );

        const partnerAvatar = (transaction as any)?.model?.avatar;
        const partnerLink = pickFirstString(
          (transaction.invoice as any)?.partner?.link,
          (transaction.invoice as any)?.partner_link,
          (transaction.invoice as any)?.partner?.profile_link,
          (transaction.invoice as any)?.company?.link,
          (transaction.invoice as any)?.campaign?.link,
          transaction.company?.link
        );

        const handleWithoutAt = partnerHandle?.startsWith('@') ? partnerHandle.slice(1) : partnerHandle;
        const modelHref = partnerLink || (handleWithoutAt ? `https://onlyfans.com/${handleWithoutAt}` : undefined);
        const modelLabel = partnerHandle;
        const shouldShowModel = Boolean(modelLabel);
        const showAvatar = Boolean(partnerAvatar && shouldShowModel);

        const orderIdFull = transaction.company?.id || transaction.invoice?.id || transaction.id;
        const orderDisplay = formatShortId(orderIdFull);

        return (
          <div className="flex flex-col gap-[4px]">
            <Chip
              style={{
                backgroundColor: `${statusChip.color}20`,
                color: statusChip.color
              }}
              className="h-[24px] px-[12px] py-[4px] text-[12px] font-medium w-fit whitespace-nowrap"
            >
              {statusChip.text}
            </Chip>
            {/* Показываем Invoice ID для invoice_payment */}
            {actionType === 'invoice_payment' && transaction.invoice?.id && (
              <div className="flex flex-wrap items-center gap-[8px]">
                <span className="text-[#11181C] text-[14px] font-semibold">
                  {t('wallet.invoiceTitle') || 'Invoice'}
                </span>
                <IdView textToCopy={transaction.invoice.id}>
                  {formatShortId(transaction.invoice.id) ?? transaction.invoice.id}
                </IdView>
              </div>
            )}
            {/* Показываем Payment ID для balance_top_up */}
            {actionType === 'balance_top_up' && transaction.transaction?.admin && (
              <div className="flex flex-wrap items-center gap-[8px]">
                <span className="text-[#11181C] text-[14px] font-semibold">
                  {t('wallet.paymentTitle') || 'Payment'}
                </span>
                <IdView textToCopy={transaction.transaction.admin}>
                  {formatShortId(transaction.transaction.admin) ?? transaction.transaction.admin}
                </IdView>
              </div>
            )}
            {/* Показываем Order ID с моделью для определенных типов действий */}
            {showOrderSection && (orderIdFull || shouldShowModel) && (
              <div className="flex flex-wrap items-center gap-[8px]">
                {orderIdFull && (
                  <div className="flex items-center gap-[6px]">
                    <span className="text-[#11181C] text-[14px] font-semibold">
                      {t('wallet.orderTitle') || 'Order'}
                    </span>
                    <IdView textToCopy={orderIdFull}>
                      {orderDisplay ?? orderIdFull}
                    </IdView>
                  </div>
                )}
                {showAvatar && (
                  <Avatar
                    src={partnerAvatar}
                    size="sm"
                    showFallback
                    name={modelLabel?.slice(1, 2)}
                    className="w-[16px] h-[16px] rounded-[4px]"
                  />
                )}
                {shouldShowModel && (
                  modelHref ? (
                    <a
                      href={modelHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#006FEE] text-[14px] font-normal"
                    >
                      {modelLabel}
                    </a>
                  ) : (
                    <span className="text-[#71717A] text-[14px] font-normal">
                      {modelLabel}
                    </span>
                  )
                )}
              </div>
            )}
          </div>
        );
      }
      case "date":
        return (
          <div className="flex items-center gap-[8px]">
            <CalendarIcon />
            <span className="text-[#71717A] text-[12px]">{formatDate(transaction.created_at)}</span>
          </div>
        );
      default:
        return null;
    }
  }, []);

  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <EmptyContentIc />
        <div className="flex flex-col gap-[6px]">
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {t('admin.creators.details.balanceHistory.empty.title')}
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {t('admin.creators.details.balanceHistory.empty.description')}
          </Typography>
        </div>
      </div>
    </div>
  );

  if (isLoading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[24px] w-full">
      <Typography variant="h6">
        История баланса
      </Typography>
      <div className="flex-1">
        <UniversalTable
        data={transactions}
        columns={columns}
        renderCell={renderCell}
        emptyContent={emptyContent}
        pagination={{
          count: totalCount,
          page_size: pageSize,
          page: currentPage,
        }}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentPage={currentPage}
        currentPageSize={pageSize}
      />
      </div>
    </div>
  );
};

export default BalanceHistoryTab;
